<template>
  <QFile
    class="sai-file"
    v-on="boundListeners"
    v-bind="boundAttrs"
    borderless
    :input-class="`text-${color}`"
    :value="value"
    :color="color"
    :clearable="clearable"
    :multiple="multiple"
    :filter="filterFn"
    :maxFiles="maxFiles"
    :maxFileSize="maxFileSize"
    :maxTotalSize="maxTotalSize"
    :hide-bottom-space="hideBottomSpace"
    no-error-icon
    ref="QFile"
    @input="updateInput"
    @rejected="rejected"
  >
    <template v-slot:prepend>
      <div
        class="row items-center full-height custom-prepend"
        @click="placeholderPickFiles"
      >
        <QIcon :color="color" :name="`eva-${icon}`" />
        <p
          :class="['q-ml-sm text-body1', `text-${color}`]"
          v-if="showPlaceholder"
        >
          {{ placeholder }}
        </p>
      </div>
    </template>
  </QFile>
</template>

<script>
import { QFile, QIcon } from 'quasar';

export default {
  name: 'SAIFile',
  props: {
    value: {
      type: undefined
    },
    placeholder: String,
    icon: {
      type: String,
      default: 'cloud-upload-outline'
    },
    clearable: {
      default: true
    },
    multiple: Boolean,
    maxFiles: Number,
    maxFileSize: Number, // As bytes
    minFileSize: {
      type: Number, // As bytes
      default: 1
    },
    filter: Function,
    maxTotalSize: Number,
    hideBottomSpace: {
      type: Boolean,
      default: true
    },
    acceptedFileTypes: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    boundListeners() {
      const { input, rejected, ...rest } = this.$listeners;
      return rest;
    },
    boundAttrs() {
      const { filter, ...rest } = this.$attrs;
      return rest;
    },
    color() {
      if (this.$attrs.disable) {
        return 'grey-6';
      }
      if (this.$attrs.error) {
        return 'negative';
      }
      return 'primary';
    },
    showPlaceholder() {
      if (!this.placeholder) {
        return false;
      }
      if (Array.isArray(this.value)) {
        return !this.value.length;
      }
      return !this.value;
    }
  },
  methods: {
    // Perform a check on the minimum file size as html input natively does not support it
    validFileSize(file) {
      return file.size >= this.minFileSize;
    },
    // Perform a case sensitive file type check as HTML accept is case insensitive
    validFileType(file) {
      if (!this.acceptedFileTypes.length) {
        return true;
      }
      const fileType = file.name.split('.').pop();
      return this.acceptedFileTypes.some(
        (acceptedType) => fileType === acceptedType
      );
    },
    filterFn(files) {
      let updatedFiles = files
        .filter(this.validFileSize)
        .filter(this.validFileType);

      if (this.filter) {
        updatedFiles = this.filter(files);
      }

      return updatedFiles;
    },
    placeholderPickFiles(e) {
      if (!this.$attrs.disable) {
        this.$refs.QFile.pickFiles(e);
      }
    },
    isMb(bytes = 0) {
      return bytes >= 1000000;
    },
    bytesToKb(bytes = 0) {
      let val = this.isMb(bytes) ? bytes / 1000000 : bytes / 1000;
      return Number(val.toFixed(2).replace('.00', ''));
    },
    getErrorMessage(error, file) {
      const incorrectTypeMessage = 'The selected file is an incorrect type';
      let size, sizeType;
      switch (error) {
        case 'filter':
          // Quasar filter fn does not provide useful/custom error messaging so we need to re test to find the message
          if (!this.validFileSize(file)) {
            return 'The selected file has no size';
          }
          if (!this.validFileType(file)) {
            return 'The selected file is not the correct type';
          }
          return 'The selected file is not valid';
        case 'max-files':
          return `You can not upload more than ${this.maxFiles} files`;
        case 'max-file-size':
          size = this.bytesToKb(this.maxFileSize);
          sizeType = this.isMb(this.maxFileSize) ? 'mb' : 'kb';
          return `The file is too large. The file size needs to be under ${size}${sizeType}`;
        case 'max-total-size':
          size = this.bytesToKb(this.maxTotalSize);
          sizeType = this.isMb(this.maxTotalSize) ? 'mb' : 'kb';
          return `The files are too large. The total file size needs to be under ${size}${sizeType}`;
        case 'accept':
          return incorrectTypeMessage;
        default:
          return error;
      }
    },
    rejectFile(failedPropValidation, file) {
      if (this.$q.notify) {
        const message = this.getErrorMessage(failedPropValidation, file);

        this.$q.notify({
          type: 'negative',
          group: `SAIFile-${message}`,
          message: message,
          position: 'bottom-left',
          actions: [{ label: 'Dismiss', color: 'white' }]
        });
      }
    },
    rejected(e) {
      this.$emit('rejected', e);
      for (let i = 0; i < e.length; i++) {
        let { failedPropValidation, file } = e[i];
        this.rejectFile(failedPropValidation, file);
      }
    },
    updateInput(newFiles) {
      // Emit the file if there was nothing or it is not a multi file picker
      if (!newFiles || !this.multiple) {
        return this.$emit('input', newFiles);
      }
      let existingFiles = Array.isArray(this.value) ? this.value : [];
      // Return the existing and new files if no maxFiles was defined
      if (isNaN(this.maxFiles)) {
        return this.$emit('input', [...existingFiles, ...newFiles]);
      }
      let remainingNumberOfFiles = this.maxFiles - newFiles.length;
      // If the user selected less files than the max, fill the available remaining files with existing ones
      if (remainingNumberOfFiles > 0) {
        let slicedFiles = existingFiles.slice(remainingNumberOfFiles * -1);
        return this.$emit('input', [...slicedFiles, ...newFiles]);
      } else {
        return this.$emit('input', [...newFiles]);
      }
    }
  },
  mounted() {
    this.pickFiles = this.$refs.QFile.pickFiles;
  },
  components: { QFile, QIcon }
};
</script>

<style lang="sass" scoped>
.sai-file::v-deep
  .q-field__bottom
    padding-top: 6px
    font-size: 13px
    color: $grey-6

  .q-field__append
    color: currentColor

  &.q-field--borderless
    .q-field__control
      padding: 0 12px

    .q-field__control:before
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='" + rgba($primary, 0.99) + "' stroke-width='1' stroke-dasharray='10%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")

  &.q-field--error
    .q-field__control:before
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='" + rgba($negative, 0.99) + "' stroke-width='1' stroke-dasharray='10%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")

  &.q-field--disabled
    .q-field__control:before
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='" + rgba($grey-6, 0.99) + "' stroke-width='1' stroke-dasharray='10%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")

  .q-field__prepend
    padding-right: 0

  .custom-prepend
    padding-right: 12px

  &:not(.q-field--disable)
    .custom-prepend
      cursor: pointer
</style>
